import { supabase } from '../config/supabase';

export const PRODUCT_IMPORT_TEMPLATES_BUCKET = 'product-import-templates';

const MIME_BY_TYPE = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
};

function inferFileType(file) {
  const lower = String(file?.name || '').toLowerCase();
  if (lower.endsWith('.xlsx')) return 'xlsx';
  if (lower.endsWith('.csv')) return 'csv';
  return null;
}

function sanitizeFilename(name, fileType) {
  const base = String(name || 'product-import-template')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .trim() || 'product-import-template';
  return `${base}.${fileType}`;
}

export async function fetchActiveProductImportTemplate(templateType = 'products') {
  const { data, error } = await supabase
    .from('product_import_templates')
    .select('id, name, file_path, file_type, is_active, created_at, uploaded_by, template_type')
    .eq('is_active', true)
    .eq('template_type', templateType)
    .maybeSingle();
  return { data, error };
}

export async function fetchActiveAmazonImportTemplate() {
  return fetchActiveProductImportTemplate('amazon_products');
}

export async function listProductImportTemplates(templateType = 'products') {
  const { data, error } = await supabase
    .from('product_import_templates')
    .select('id, name, file_path, file_type, is_active, created_at, updated_at, uploaded_by, template_type')
    .eq('template_type', templateType)
    .order('created_at', { ascending: false });
  return { data: data ?? [], error };
}

export async function listAmazonImportTemplates() {
  return listProductImportTemplates('amazon_products');
}

export async function uploadProductImportTemplate(file, displayName, templateType = 'products') {
  const fileType = inferFileType(file);
  if (!fileType) {
    return { success: false, error: 'File must be .xlsx or .csv' };
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) {
    return { success: false, error: 'Not signed in' };
  }

  const ext = fileType;
  const objectPath = `global/${crypto.randomUUID()}.${ext}`;
  const contentType = file.type || MIME_BY_TYPE[fileType];

  const { error: uploadError } = await supabase.storage
    .from(PRODUCT_IMPORT_TEMPLATES_BUCKET)
    .upload(objectPath, file, {
      contentType,
      upsert: false,
    });

  if (uploadError) {
    return { success: false, error: uploadError.message };
  }

  const { data, error: insertError } = await supabase
    .from('product_import_templates')
    .insert([{
      name: String(displayName || file.name || 'Product import template').trim(),
      file_path: objectPath,
      file_type: fileType,
      is_active: false,
      uploaded_by: user.id,
      template_type: templateType,
    }])
    .select()
    .single();

  if (insertError) {
    await supabase.storage.from(PRODUCT_IMPORT_TEMPLATES_BUCKET).remove([objectPath]);
    return { success: false, error: insertError.message };
  }

  return { success: true, data };
}

export async function uploadAmazonImportTemplate(file, displayName) {
  return uploadProductImportTemplate(file, displayName, 'amazon_products');
}

export async function activateProductImportTemplate(templateId) {
  const { error } = await supabase.rpc('activate_product_import_template', {
    p_template_id: templateId,
  });
  if (error) return { success: false, error: error.message };
  return { success: true };
}

export async function downloadActiveTemplateBlob(templateType = 'products') {
  const { data: row, error: metaError } = await fetchActiveProductImportTemplate(templateType);
  if (metaError) return { success: false, error: metaError.message };
  if (!row?.file_path) {
    return { success: false, error: 'No active template' };
  }

  const { data, error } = await supabase.storage
    .from(PRODUCT_IMPORT_TEMPLATES_BUCKET)
    .download(row.file_path);

  if (error) return { success: false, error: error.message };

  const filename = sanitizeFilename(row.name, row.file_type);
  return { success: true, blob: data, filename, template: row };
}

function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadActiveTemplateToDisk(templateType = 'products') {
  const result = await downloadActiveTemplateBlob(templateType);
  if (!result.success) return result;
  triggerBlobDownload(result.blob, result.filename);
  return { success: true, filename: result.filename };
}

export async function deleteProductImportTemplate(templateId, filePath) {
  const { error: delDb } = await supabase
    .from('product_import_templates')
    .delete()
    .eq('id', templateId);
  if (delDb) return { success: false, error: delDb.message };

  if (filePath) {
    await supabase.storage.from(PRODUCT_IMPORT_TEMPLATES_BUCKET).remove([filePath]);
  }
  return { success: true };
}
